import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useCustomerAuth } from './CustomerAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WishlistContext = createContext(null);

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const { customerToken, isAuthenticated } = useCustomerAuth();
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [wishlistIds, setWishlistIds] = useState(new Set());

  const getAuthHeader = useCallback(() => {
    if (customerToken) {
      return { Authorization: `Bearer ${customerToken}` };
    }
    return {};
  }, [customerToken]);

  const fetchWishlist = useCallback(async () => {
    if (!isAuthenticated) {
      setWishlistItems([]);
      setWishlistIds(new Set());
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/wishlist`, {
        headers: getAuthHeader()
      });
      setWishlistItems(response.data);
      setWishlistIds(new Set(response.data.map(item => item.shrimp_id)));
    } catch (error) {
      console.error('Failed to fetch wishlist:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAuthHeader]);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  const addToWishlist = async (shrimpId) => {
    if (!isAuthenticated) {
      return { success: false, requiresAuth: true };
    }
    
    try {
      const response = await axios.post(`${API}/wishlist`, 
        { shrimp_id: shrimpId },
        { headers: getAuthHeader() }
      );
      setWishlistItems(prev => [response.data, ...prev]);
      setWishlistIds(prev => new Set([...prev, shrimpId]));
      return { success: true };
    } catch (error) {
      if (error.response?.status === 400) {
        return { success: false, message: 'Already in wishlist' };
      }
      return { success: false, message: error.response?.data?.detail || 'Failed to add' };
    }
  };

  const removeFromWishlist = async (shrimpId) => {
    try {
      await axios.delete(`${API}/wishlist/${shrimpId}`, {
        headers: getAuthHeader()
      });
      setWishlistItems(prev => prev.filter(item => item.shrimp_id !== shrimpId));
      setWishlistIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(shrimpId);
        return newSet;
      });
      return { success: true };
    } catch (error) {
      return { success: false, message: error.response?.data?.detail || 'Failed to remove' };
    }
  };

  const isInWishlist = useCallback((shrimpId) => {
    return wishlistIds.has(shrimpId);
  }, [wishlistIds]);

  const toggleWishlist = async (shrimpId) => {
    if (isInWishlist(shrimpId)) {
      return removeFromWishlist(shrimpId);
    } else {
      return addToWishlist(shrimpId);
    }
  };

  return (
    <WishlistContext.Provider value={{
      wishlistItems,
      loading,
      wishlistCount: wishlistItems.length,
      addToWishlist,
      removeFromWishlist,
      isInWishlist,
      toggleWishlist,
      refreshWishlist: fetchWishlist
    }}>
      {children}
    </WishlistContext.Provider>
  );
};
